import React, { useEffect, useMemo, useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import {
  Combobox,
  ComboboxOption,
  ComboboxOptionGroup,
  ComboboxOptionLabel,
  Option,
  Select,
} from '@fiverr-private/forms';
import { Gql } from '@fiverr-private/dynamic_matching';
import { translate } from '@fiverr-private/i18n-react';
import { IconButton, TextButton } from '@fiverr-private/button';
import { PlusIcon, BinIcon } from '@fiverr-private/icons';
import { Values } from '@fiverr-private/dropdown';
import { FreelancerLanguageMinProficiency, FreelancerLanguagePreference } from '../../../utils/types';
import { T_LANGUAGE_PREFIX } from '../../../utils/consts';
import { useIdealCandidateContext } from '../../../utils/context';
import { AppI18n } from '../../../../../../i18n';
import { useLanguagesOptions } from './useLanguagesOptions';

const DEFAULT_LANGUAGE_PREFERENCE: FreelancerLanguagePreference = {
  languageCode: 'zz',
  minProficiency: FreelancerLanguageMinProficiency.Conversational,
};

const LanguageSelect = ({
  code,
  onChange,
  selectedLanguages,
}: {
  code?: string;
  onChange: (code: string) => void;
  selectedLanguages: FreelancerLanguagePreference[];
}) => {
  const [inputValue, setInputValue] = useState<string | undefined>('');
  const [selectedLanguage, setSelectedLanguage] = useState<Values>({
    item: code !== 'zz' ? code : '',
  });
  const { languagesOptions, mappedLanguages } = useLanguagesOptions({
    selectedLanguages,
    currentSelected: selectedLanguage,
  });

  const handleChange = (value: Values) => {
    setSelectedLanguage(value);
    onChange(value.item ? (value.item as string) : '');
  };

  useEffect(() => {
    if (!code || code === 'zz') {
      setInputValue('');
      setSelectedLanguage({ item: '' });
    } else {
      setSelectedLanguage({ item: code });
      setInputValue(mappedLanguages[code]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <Combobox
      value={selectedLanguage}
      onChange={handleChange}
      placeholder={translate(`${T_LANGUAGE_PREFIX}.language_placeholder`) as string}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      hideClearButton
      height="42px"
    >
      <ComboboxOptionGroup>
        {languagesOptions.map(({ label, value }) => (
          <ComboboxOption key={value} value={value}>
            <ComboboxOptionLabel>{label}</ComboboxOptionLabel>
          </ComboboxOption>
        ))}
      </ComboboxOptionGroup>
    </Combobox>
  );
};

const LevelSelect = ({
  level,
  onChange,
}: {
  level: Gql.LanguageProficiencyLevel;
  onChange: (level: Gql.LanguageProficiencyLevel) => void;
}) => {
  const levels = Object.values(Gql.LanguageProficiencyLevel);

  return (
    <Select
      value={{ item: level }}
      onChange={(value) => onChange(value.item as Gql.LanguageProficiencyLevel)}
      fullWidth={true}
    >
      {levels.map((level, index) => (
        <Option key={index} value={level}>
          {translate(`${T_LANGUAGE_PREFIX}.levels.${level.toLocaleLowerCase()}`)}
        </Option>
      ))}
    </Select>
  );
};

export const Languages = () => {
  const { languages, setLanguages } = useIdealCandidateContext();
  const languagesPreference = useMemo(
    () => (languages?.length > 0 ? languages : [DEFAULT_LANGUAGE_PREFERENCE]),
    [languages]
  );

  const handleAdd = () => setLanguages([...languagesPreference, DEFAULT_LANGUAGE_PREFERENCE]);

  const handleChange = (index: number, preference: FreelancerLanguagePreference) =>
    setLanguages(languagesPreference.map((p, i) => (i === index ? preference : p)));

  const handleRemove = (index: number) => {
    const updated = languagesPreference.filter((_, i) => i !== index);
    setLanguages(updated);
  };

  return (
    <Stack direction="column" paddingX="0.5" gap="4">
      <Typography size="b_sm" color="grey_900" fontWeight="normal">
        <AppI18n k={`sections.ideal_candidate.languages.subtitle`} />
      </Typography>
      <Stack direction="column" gap={{ default: '8', sm: '4' }}>
        {languagesPreference.map((preference, index) => (
          <Stack direction={{ default: 'column', sm: 'row' }} gap="4" key={index}>
            <Stack minWidth="200px" width="100%">
              <LanguageSelect
                key={index}
                selectedLanguages={languagesPreference}
                code={preference.languageCode}
                onChange={(code) => handleChange(index, { ...preference, languageCode: code })}
              />
            </Stack>
            <Stack minWidth="200px" width="100%">
              <LevelSelect
                level={preference.minProficiency}
                onChange={(level) => handleChange(index, { ...preference, minProficiency: level })}
              />
            </Stack>
            <Stack alignItems="center">
              <IconButton aria-label="Remove" variant="ghost" shape="circle" onClick={() => handleRemove(index)}>
                <BinIcon />
              </IconButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      {languagesPreference.length < 3 && (
        <Container justifyContent="flexStart">
          <TextButton onClick={handleAdd}>
            <PlusIcon />
            <AppI18n k={`sections.ideal_candidate.languages.add_language`} />
          </TextButton>
        </Container>
      )}
    </Stack>
  );
};
